import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Avatar,
  Box,
  Button,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { AccountCircle, ExitToApp, Settings, Refresh } from '@mui/icons-material';
import MicNoneIcon from '@mui/icons-material/MicNone';
import MicrophoneSettingsDialog from '../common/MicrophoneSettingsDialog';
// import RocketIcon from '@mui/icons-material/RocketLaunch';
// import AIDialog from '../common/AIDialog';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import AppearanceDialog from '../settings/AppearanceDialog';
import projectService from '../../services/projectService';
import { setDnd as apiSetDnd } from '../../services/presenceService';

/**
 * Komponent profilu użytkownika
 */
const UserProfile: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [dnd, setDnd] = React.useState<boolean>(() => {
    try { return (localStorage.getItem('chat:dnd') || '0') === '1'; } catch { return false; }
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [appearanceOpen, setAppearanceOpen] = React.useState(false);
  // const [aiOpen, setAiOpen] = React.useState(false);
  const [micOpen, setMicOpen] = React.useState(false);

  // Obsługa kliknięcia przycisku profilu
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Obsługa zamknięcia menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDnd = (_: any, value: boolean) => {
    setDnd(value);
    try { localStorage.setItem('chat:dnd', value ? '1' : '0'); } catch {}
    try { const uid = user?.user_id; if (uid) localStorage.setItem(`dnd:${uid}`, value ? '1' : '0'); } catch {}
    try { apiSetDnd(value); } catch {}
    window.dispatchEvent(new CustomEvent('chat:dnd', { detail: { enabled: value } }));
  };

  // Po załadowaniu zsynchronizuj bieżący stan DND do backendu (aby był widoczny dla innych)
  React.useEffect(() => {
    try { apiSetDnd(dnd); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Obsługa wylogowania
  const handleLogout = () => {
    handleClose();
    logout();
  };

  // Ręczne odświeżenie danych finansowych (wszystkie projekty)
  const handleRefreshFinancials = async () => {
    try {
      handleClose();
      await projectService.updateFinancialData();
      // Opcjonalnie można dać prosty feedback
      console.log('Zainicjowano odświeżenie danych finansowych projektów.');
    } catch (e) {
      console.error('Błąd odświeżania danych finansowych:', e);
    }
  };

  // Jeśli użytkownik nie jest zalogowany, nie wyświetlaj nic
  if (!isAuthenticated || !user) {
    return null;
  }

  // Inicjały użytkownika (do wyświetlenia, gdy nie ma avatara)
  const getInitials = () => {
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`;
    } else if (user.display_name) {
      return user.display_name.charAt(0);
    } else {
      return user.email.charAt(0).toUpperCase();
    }
  };

  // Nazwa użytkownika do wyświetlenia
  const displayName = user.display_name || user.email;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ ml: 1 }}
        >
          <Box sx={{ position: 'relative' }}>
            {user.avatar_url ? (
              <Avatar
                src={user.avatar_url}
                alt={displayName}
                sx={{ width: 32, height: 32 }}
              />
            ) : (
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {getInitials()}
              </Avatar>
            )}
            {dnd && (
              <Box sx={{ position: 'absolute', right: -1, bottom: -1, width: 10, height: 10, bgcolor: 'error.main', borderRadius: '50%', boxShadow: '0 0 0 2px #fff' }} />
            )}
          </Box>
        </IconButton>
      
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'user-button',
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
          {user.telefon && (
            <Typography variant="body2" color="text.secondary">
              Tel: {user.telefon}
            </Typography>
          )}
          {user.position && (
            <Typography variant="body2" color="text.secondary">
              Stanowisko: {user.position}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Rola: {user.role}
          </Typography>
        </Box>
        
        <Divider />
        <MenuItem>
          <FormControlLabel control={<Switch checked={dnd} onChange={toggleDnd} size="small" />} label="Nie przeszkadzać" />
        </MenuItem>
        
        <MenuItem onClick={handleRefreshFinancials}>
          <Refresh sx={{ mr: 1 }} />
          Odśwież dane
        </MenuItem>

        <MenuItem onClick={() => { handleClose(); window.location.href = '/profile'; }}>
          <AccountCircle sx={{ mr: 1 }} />
          Mój profil
        </MenuItem>
        
        <MenuItem onClick={() => { handleClose(); setAppearanceOpen(true); }}>
          <Settings sx={{ mr: 1 }} />
          Wygląd
        </MenuItem>

        <MenuItem onClick={() => { handleClose(); setMicOpen(true); }}>
          <MicNoneIcon sx={{ mr: 1 }} />
          Mikrofon
        </MenuItem>


        <MenuItem onClick={() => { handleClose(); window.dispatchEvent(new CustomEvent('ui:shortcuts:open')); }}>
          <KeyboardIcon sx={{ mr: 1 }} />
          Skróty klawiszowe
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleLogout}>
          <ExitToApp sx={{ mr: 1 }} />
          Wyloguj się
        </MenuItem>
      </Menu>
      <AppearanceDialog open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
      <MicrophoneSettingsDialog open={micOpen} onClose={() => setMicOpen(false)} />
      {/* <AIDialog open={aiOpen} onClose={() => setAiOpen(false)} /> */}
    </Box>
  );
};

export default UserProfile;
